import React, { ReactElement, useEffect, useState } from 'react';
import styled from 'styled-components';
import { Border, Color, Grid, Shadow, Type } from '../../app/StyleGuide';
import AccessDenied from '../../components/AccessDenied';
import Breadcrumbs from '../../components/Breadcrumbs';
import { CurriculumImage } from '../../components/CurriculumImage';
import ErrorMessage from '../../components/ErrorMessage';
import { Container } from '../../components/Layout';
import LoadingState from '../../components/LoadingState';
import SectionHeader from '../../components/SectionHeader';
import useModal from '../../hooks/useModal';
import cacheService from '../../services/cacheService';
import windowService from '../../services/windowService';
import { CurriculumPricing } from '../curriculum';
import LicenseListItem from './LicenseListItem';
import { SelectSubscriptionLicenseModal } from './SelectSubscriptionLicenseModal';
import SwitchCurriculumConfirmationOverlay from './components/SwitchCurriculumConfirmationOverlay';
import { UpcomingSubscriptionRow } from './components/UpcomingSubscriptionRow';
import useFetchSubscriptions, { LicenseWithCurriculum } from './hooks/useFetchSubscriptions';
import { License } from './models/license';
import { findCurriculumForItemNumber } from './utils/findCurriculumForItemNumber';

const LicenseList = styled.div`
  display: grid;
  grid-gap: ${Grid._4};
  margin-bottom: ${Grid._11};
`;

const LicenseCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Grid._4};
  background: white;
  font-weight: ${Type.Weight.bold};
  color: ${Type.Color.dark};
  border: 1px solid ${Color.Gray._10};
  border-radius: ${Border.radius};
  box-shadow: ${Shadow.small};
`;

const LicenseListSection = ({
  title,
  licenses,
  curriculum,
  upcomingLicenses,
  onItemClick,
  onUpcomingSubscriptionClick,
}: {
  title: string;
  licenses: LicenseWithCurriculum[];
  curriculum: CurriculumPricing[];
  upcomingLicenses: LicenseWithCurriculum[];
  onItemClick?: (license: License) => void;
  onUpcomingSubscriptionClick: (props: {
    oldPlanItemNumber: string;
    newPlanItemNumber: string;
    renewalDate: string;
  }) => void;
}) => {
  return (
    <>
      <SectionHeader title={title} />
      <LicenseList data-qa-hook={`licenseList${title}`}>
        {licenses.map(license => {
          const matchingUpcomingLicense = upcomingLicenses.find(
            l => l.subscription?.id === license.source?.previousSubscriptionID
          );
          const upcomingCurriculum = matchingUpcomingLicense
            ? findCurriculumForItemNumber(curriculum, matchingUpcomingLicense.item?.number)
            : undefined;

          const oldPlanItemNumber = matchingUpcomingLicense?.item?.number;
          const newPlanItemNumber = matchingUpcomingLicense?.subscription?.item?.number;
          const startDate = matchingUpcomingLicense?.startDate;
          return (
            <LicenseCard key={license.id}>
              <LicenseListItem
                key={license.id}
                license={license}
                onClick={() => onItemClick?.(license)}
                hasUpcomingSubscription={!!matchingUpcomingLicense}
              />
              {matchingUpcomingLicense && oldPlanItemNumber && newPlanItemNumber && startDate && (
                <UpcomingSubscriptionRow
                  brandCode={upcomingCurriculum?.brand.code ?? ''}
                  title={matchingUpcomingLicense.item.title}
                  startDate={startDate}
                  onClick={() =>
                    onUpcomingSubscriptionClick?.({
                      oldPlanItemNumber,
                      newPlanItemNumber,
                      renewalDate: startDate,
                    })
                  }
                />
              )}
            </LicenseCard>
          );
        })}
      </LicenseList>
    </>
  );
};

type SelectModalPayload = {
  subscriptionImage: ReactElement;
  subscriptionName: string;
  licenses: License[];
};

export function ManageSubscriptions() {
  const {
    isLoading,
    isError,
    isAccessDenied,
    allLicenses,
    activeLicenses,
    expiredLicenses,
    curriculum,
    upcomingLicenses,
  } = useFetchSubscriptions();
  const crumbs = [{ name: 'Organization', route: '#/org' }, { name: 'Purchases and Subscriptions' }];
  const [switchCurriculumConfirmation, setSwitchCurriculumConfirmation] = useState<{
    open: boolean;
    oldPlanItemNumber?: string;
    newPlanItemNumber?: string;
    renewalDate?: string;
  }>({
    open: false,
  });
  const curriculumSwitchData: { oldPlan?: string; newPlan?: string; renewalDate?: string } =
    cacheService.get('subscriptionChanged');

  const [modal, openModal] = useModal(
    (type: string | undefined, payload: SelectModalPayload | undefined, dismissModal: () => void) => {
      if (!type) return null;
      return payload ? <SelectSubscriptionLicenseModal {...payload} handleDismiss={dismissModal} /> : null;
    }
  );

  const redirectToManageLicense = (licenseId: string) =>
    windowService.redirectTo(`/org/purchases-and-subscriptions/${licenseId}`);

  const handleClick = (license: LicenseWithCurriculum) => {
    if (!license.subscription) return redirectToManageLicense(license.id);

    const subscriptionLicenses = allLicenses?.filter(l => l.subscription?.id === license.subscription?.id);

    if (subscriptionLicenses?.length === 1) return redirectToManageLicense(license.id);

    const curriculumBrandCode = license.curriculum?.brand?.code;
    const subscriptionImage = <CurriculumImage brandCode={curriculumBrandCode} />;
    const subscriptionName = license.subscription?.item?.title ?? license.name;
    openModal('select', { subscriptionImage, subscriptionName, licenses: subscriptionLicenses });
  };

  useEffect(() => {
    if (curriculumSwitchData) {
      setSwitchCurriculumConfirmation({
        open: true,
        oldPlanItemNumber: curriculumSwitchData.oldPlan,
        newPlanItemNumber: curriculumSwitchData.newPlan,
        renewalDate: curriculumSwitchData.renewalDate,
      });
    }
    return () => {
      cacheService.remove('subscriptionChanged');
    };
  }, []);

  return (
    <>
      <Breadcrumbs crumbs={crumbs} />
      <Container data-qa-hook="subscriptionsView">
        <h2 className="mt-40 mb-48">Purchases and Subscriptions</h2>
        {isLoading ? (
          <LoadingState />
        ) : isError ? (
          <ErrorMessage>
            A problem occurred showing this page. Please refresh the page to try again. <a href="#/help">Contact Us</a>
          </ErrorMessage>
        ) : isAccessDenied ? (
          <AccessDenied />
        ) : (
          <>
            {!activeLicenses?.length && !expiredLicenses?.length && <p>No Purchases or Subscriptions</p>}
            {!!activeLicenses?.length && (
              <LicenseListSection
                title="Active"
                licenses={activeLicenses}
                curriculum={curriculum ?? []}
                upcomingLicenses={upcomingLicenses ?? []}
                onItemClick={handleClick}
                onUpcomingSubscriptionClick={({ oldPlanItemNumber, newPlanItemNumber, renewalDate }) =>
                  setSwitchCurriculumConfirmation({ open: true, oldPlanItemNumber, newPlanItemNumber, renewalDate })
                }
              />
            )}

            {!!expiredLicenses?.length && (
              <LicenseListSection
                title="Expired"
                licenses={expiredLicenses}
                curriculum={curriculum ?? []}
                upcomingLicenses={upcomingLicenses ?? []}
                onUpcomingSubscriptionClick={({ oldPlanItemNumber, newPlanItemNumber, renewalDate }) =>
                  setSwitchCurriculumConfirmation({ open: true, oldPlanItemNumber, newPlanItemNumber, renewalDate })
                }
              />
            )}
          </>
        )}
        {modal}
        {switchCurriculumConfirmation.oldPlanItemNumber &&
          switchCurriculumConfirmation.newPlanItemNumber &&
          switchCurriculumConfirmation.renewalDate && (
            <SwitchCurriculumConfirmationOverlay
              open={switchCurriculumConfirmation.open}
              dismiss={() => setSwitchCurriculumConfirmation({ open: false })}
              oldPlanItemNumber={switchCurriculumConfirmation.oldPlanItemNumber}
              newPlanItemNumber={switchCurriculumConfirmation.newPlanItemNumber}
              renewalDate={switchCurriculumConfirmation.renewalDate}
            />
          )}
      </Container>
    </>
  );
}
